import type { Scene } from '@lumen5/luminary';

import { getSceneTranscriptTokens } from '../tools/transcript-partition';

import type { RootState } from 'js/src/libs/RootState';
import { translateTranscriptTimeToSceneGroupTime } from 'js/src/luminary/luminary-selectors';

/** A narration word placed within its scene, as start/end seconds from the scene's start. */
export interface TimedWord {
    word: string;
    startSeconds: number;
    endSeconds: number;
}

/** The words spanning a captured frame, with the one being spoken at that instant marked. */
export interface NarrationWindow {
    /** The words spanning the frame, by sequence order, boundaries inclusive. */
    words: TimedWord[];
    /** Index within `words` of the word being spoken at the frame, or -1 in a silent gap. */
    activeIndex: number;
}

/**
 * The current scene's voiceover narration, tagged with when each word is spoken so the agent can
 * sync its timeline to the speech. Timings are seconds from the scene's start — the same unit the
 * agent positions its tweens in, so a cue can be used as a tween position directly. Returns '' for
 * a scene with no narration (e.g. text-on-media) so the section is omitted; falls back to plain
 * text when the scene isn't voicedriven and word timings can't be mapped.
 */
export function formatSceneNarration(scene: Scene, state: RootState): string {
    const spoken = (getSceneTranscriptTokens(state, scene) ?? []).filter((token) => !token.isPause);
    if (!spoken.length) {
        return '';
    }
    const timed = timeNarrationWords(scene, spoken);
    return timed ? formatTimedNarration(timed) : formatNarration(narrationText(spoken));
}

function narrationText(spoken: ReadonlyArray<{ word: string }>): string {
    return spoken.map((token) => token.word).join(' ');
}

/**
 * Map each narration word's start and end onto seconds from the scene's start — the unit the agent
 * authors its timeline in. Words arrive in transcript (source-audio) time; convert to scene-group
 * time, which is cut-aware, then rebase against the scene's own window. Returns null when the scene group has no playback
 * regions to map against, so the caller can fall back to plain text.
 */
export function timeNarrationWords(
    scene: Scene,
    words: ReadonlyArray<{ word: string; startTime: number; endTime: number }>,
): TimedWord[] | null {
    const sceneGroup = scene.sceneGroup;
    const regions = sceneGroup?.voicedrivenAssetPlaybackRegions;
    const baseDuration = sceneGroup?.voicedrivenAssetDuration;
    if (!regions?.length || !baseDuration || scene.duration <= 0) {
        return null;
    }
    const sceneStart = scene.startTimeRelativeToSceneGroup;
    const sceneDurationMs = scene.duration;
    const toSeconds = (transcriptTime: number): number => {
        const sceneGroupTime = translateTranscriptTimeToSceneGroupTime(
            transcriptTime,
            regions,
            baseDuration,
        );
        const offsetMs = Math.min(sceneDurationMs, Math.max(0, sceneGroupTime - sceneStart));
        return offsetMs / 1000;
    };
    return words.map(({ word, startTime, endTime }) => ({
        word,
        startSeconds: toSeconds(startTime),
        endSeconds: toSeconds(endTime),
    }));
}

/**
 * The narration around scene second `S`: the word that most recently finished (largest endSeconds ≤
 * S), the first word not yet started (smallest startSeconds > S), and every word between them
 * inclusive. Clamps to the first/last word when the frame precedes or follows all speech, so the
 * window is never empty when the scene has words. `activeIndex` marks the word actually being spoken
 * at `S`, or -1 when `S` lands in a silence between words. Null only when there are no words. Pure
 * over the timed words.
 */
export function narrationWindowAt(timed: TimedWord[], seconds: number): NarrationWindow | null {
    if (!timed.length) {
        return null;
    }
    let firstIndex: number | null = null;
    let firstEnd = -Infinity;
    let lastIndex: number | null = null;
    let lastStart = Infinity;
    timed.forEach((word, index) => {
        if (word.endSeconds <= seconds && word.endSeconds > firstEnd) {
            firstEnd = word.endSeconds;
            firstIndex = index;
        }
        if (word.startSeconds > seconds && word.startSeconds < lastStart) {
            lastStart = word.startSeconds;
            lastIndex = index;
        }
    });
    const start = firstIndex ?? 0;
    const end = lastIndex ?? timed.length - 1;
    const words = timed.slice(start, end + 1);
    const activeIndex = words.findIndex(
        (word) => word.startSeconds <= seconds && seconds < word.endSeconds,
    );
    return { words, activeIndex };
}

/**
 * Render a narration window as its words space-joined, wrapping the currently-spoken word in `**` so
 * the model can tell "now" from the flanking context. A silent gap (no active word) is left unmarked.
 */
export function formatNarrationWindow(window: NarrationWindow): string {
    return window.words
        .map((word, index) => (index === window.activeIndex ? `**${word.word}**` : word.word))
        .join(' ');
}

/**
 * Render timed narration as inline `[N.Ns] word` cues (one scene's worth), so the agent can read the
 * sentence and see exactly which second each word lands on. Pure over the timed words.
 */
export function formatTimedNarration(
    words: ReadonlyArray<{ word: string; startSeconds: number }>,
): string {
    const line = words
        .map(({ word, startSeconds }) => `[${startSeconds.toFixed(1)}s] ${word}`)
        .join(' ');
    return `## Narration during this scene ([N.Ns] marks the second each word is spoken, measured from the scene's start — position your tweens at these seconds)\n${line}`;
}

/**
 * Plain (untimed) narration, the fallback when word timings can't be mapped. Pure over the string;
 * returns '' when empty so the section is omitted.
 */
export function formatNarration(narration: string): string {
    const trimmed = narration.trim();
    if (!trimmed) {
        return '';
    }
    return `## Narration during this scene (the voiceover spoken while your graphic is on screen — design to complement it)\n"${trimmed}"`;
}
